import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Instagram, Facebook, Calendar } from 'lucide-react';
import { CITIES } from '../cities';
import { EVENT_TYPES } from '../eventTypes';
import { WHATSAPP_NUMBER, INSTAGRAM_URL, FACEBOOK_URL, TIKTOK_URL } from '../config';
import { foto } from '../imagenes';

/**
 * Orden de la navegación: qué alquilamos → para qué evento → dónde llegamos →
 * cómo comprarlo → cuánto vale. Los desplegables van en medio porque son los
 * dos conjuntos de subpáginas del sitio.
 */
const NAV_START = [{ to: '/catalogo', label: 'Cabinas' }];

const NAV_END = [
  { to: '/venta', label: 'Venta' },
  { to: '/precios', label: 'Precios' },
];

const MENUS = {
  eventos: {
    label: 'Eventos',
    to: '/eventos',
    heading: 'Tipos de evento',
    allLabel: 'Ver todos los eventos',
    items: EVENT_TYPES.map((e) => ({ to: `/eventos/${e.slug}`, label: e.name })),
  },
  alquiler: {
    label: 'Alquiler',
    to: '/ciudades',
    heading: 'Destinos populares',
    allLabel: 'Explorar todo',
    items: CITIES.map((c) => ({ to: `/ciudades/${c.slug}`, label: c.name })),
  },
} as const;

type MenuKey = keyof typeof MENUS;

/** Desplegable de la barra: el enlace del encabezado es real y navegable. */
const NavDropdown: React.FC<{
  menu: (typeof MENUS)[MenuKey];
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}> = ({ menu, open, onOpen, onClose }) => (
  <div className="group relative" onMouseEnter={onOpen} onMouseLeave={onClose}>
    <NavLink
      to={menu.to}
      className={({ isActive }) => `nav-link flex items-center ${isActive ? 'nav-link--active' : ''}`}
      aria-haspopup="true"
      aria-expanded={open}
      onFocus={onOpen}
    >
      {menu.label}
      <ChevronDown
        size={14}
        className="ml-1.5 opacity-40 transition-transform duration-300 group-hover:rotate-180"
      />
    </NavLink>

    <div
      className={`absolute left-1/2 top-full mt-2 w-64 origin-top -translate-x-1/2 rounded-2xl bg-cabina-cream py-5 shadow-2xl transition-all duration-500 ${
        open
          ? 'translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none -translate-y-4 scale-95 opacity-0'
      }`}
    >
      <div className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-l border-t border-gray-50 bg-cabina-cream" />
      <div className="relative z-10">
        <p className="mb-3 px-8 py-2 text-[10px] font-black uppercase italic tracking-[0.3em] text-cabina-red/60">
          {menu.heading}
        </p>
        {menu.items.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="block w-full px-8 py-2.5 text-left text-sm font-medium text-gray-800 transition-all duration-300 hover:bg-white/50 hover:pl-10 hover:text-cabina-red"
          >
            {item.label}
          </Link>
        ))}
        <div className="mt-3 border-t border-gray-100/50 pt-3">
          <Link
            to={menu.to}
            className="block w-full px-8 py-2 text-left text-[10px] font-black uppercase tracking-[0.2em] text-cabina-red hover:opacity-70"
          >
            {menu.allLabel}
          </Link>
        </div>
      </div>
    </div>
  </div>
);

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <header className="sticky top-0 z-50 shadow-sm">
      {/* ── Barra superior roja ── */}
      <div className="bg-cabina-red px-6 py-2.5 text-white">
        <div className="container mx-auto flex flex-col items-center justify-between gap-3 md:flex-row">
          <span className="hidden font-sans text-[11px] font-medium uppercase tracking-[0.25em] text-white/90 md:block md:text-sm">
            ¡Inmortalizamos tus mejores momentos!
          </span>
          <div className="md:hidden" />

          <div className="flex w-full items-center justify-between gap-6 md:w-auto md:justify-end">
            <div className="flex items-center gap-4 border-r border-white/30 pr-4 md:gap-5 md:pr-6">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-white transition-all duration-300 hover:scale-125 hover:opacity-80">
                <Instagram size={18} />
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-white transition-all duration-300 hover:scale-125 hover:opacity-80">
                <Facebook size={18} />
              </a>
              <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="text-white transition-all duration-300 hover:scale-125 hover:opacity-80">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-5.201 1.743l-.002-.001.002.001a2.895 2.895 0 0 1 3.183-4.51v-3.5a6.329 6.329 0 0 0-5.394 10.692 6.33 6.33 0 0 0 10.857-4.424V8.687a8.182 8.182 0 0 0 4.773 1.526V6.79a4.831 4.831 0 0 1-1.003-.104z" />
                </svg>
              </a>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="text-white transition-all duration-300 hover:scale-125 hover:opacity-80">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                </svg>
              </a>
            </div>
            <Link
              to="/contacto"
              className="group flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:text-cabina-cream md:text-sm"
            >
              <Calendar size={18} className="transition-transform group-hover:rotate-12" />
              <span>Reservar ahora</span>
            </Link>
          </div>
        </div>
      </div>

      {/* ── Barra principal ── */}
      <div
        className={`border-b transition-all duration-500 ${
          isScrolled
            ? 'border-gray-200 bg-cabina-cream py-1 shadow-md'
            : 'border-gray-100 bg-cabina-cream/95 py-0 backdrop-blur-lg'
        }`}
      >
        <div className="container mx-auto flex h-20 items-center justify-between px-6 md:h-24">
          <Link to="/" className="group flex items-center" aria-label="Cabina de Fotos — inicio">
            <img
              {...foto('/images/logo.png', '240px')}
              alt="Cabina de Fotos Colombia"
              width={220}
              height={64}
              className="h-9 w-auto object-contain transition-all duration-500 group-hover:scale-105 group-hover:drop-shadow-md md:h-12"
            />
          </Link>

          {/* Navegación de escritorio */}
          <nav className="hidden items-center gap-9 lg:flex xl:gap-11">
            <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
              Inicio
            </NavLink>

            {NAV_START.map((l) => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
                {l.label}
              </NavLink>
            ))}

            {(['eventos', 'alquiler'] as MenuKey[]).map((key) => (
              <NavDropdown
                key={key}
                menu={MENUS[key]}
                open={openMenu === key}
                onOpen={() => setOpenMenu(key)}
                onClose={() => setOpenMenu(null)}
              />
            ))}

            {NAV_END.map((l) => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}>
                {l.label}
              </NavLink>
            ))}

            <Link
              to="/contacto"
              className="group/btn relative overflow-hidden rounded-full bg-cabina-red px-9 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-white shadow-[0_10px_20px_rgba(200,16,46,0.2)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_15px_30px_rgba(200,16,46,0.3)]"
            >
              <span className="relative z-10">Contacto</span>
              <span className="absolute inset-0 translate-y-full bg-black transition-transform duration-300 group-hover/btn:translate-y-0" />
            </Link>
          </nav>

          <button
            className="rounded-full p-2 text-black transition-all hover:bg-cabina-red/5 lg:hidden"
            onClick={() => setIsOpen(true)}
            aria-label="Abrir menú"
            aria-expanded={isOpen}
          >
            <Menu size={28} />
          </button>
        </div>
      </div>

      {/* ── Menú móvil ── */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] animate-fade-in overflow-y-auto bg-cabina-cream lg:hidden">
          <div className="container mx-auto flex h-full flex-col px-8 py-12">
            <div className="mb-14 flex items-center justify-between">
              <img {...foto('/images/logo.png', '240px')} alt="Cabina de Fotos" className="h-10 w-auto" />
              <button
                onClick={() => setIsOpen(false)}
                aria-label="Cerrar menú"
                className="rounded-full bg-white/70 p-3 text-gray-400 transition-colors hover:text-cabina-red"
              >
                <X size={30} />
              </button>
            </div>

            <nav className="flex flex-col space-y-7">
              <Link to="/" className="text-left font-serif text-3xl italic text-cabina-red">
                Inicio
              </Link>
              {[
                ...NAV_START,
                { to: '/eventos', label: 'Eventos' },
                { to: '/ciudades', label: 'Alquiler' },
                ...NAV_END,
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="text-left text-lg font-bold uppercase tracking-[0.2em] text-gray-900"
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            {(['eventos', 'alquiler'] as MenuKey[]).map((key) => (
              <div key={key}>
                <p className="mb-4 mt-10 text-[10px] font-black uppercase italic tracking-[0.3em] text-cabina-red/60">
                  {MENUS[key].heading}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {MENUS[key].items.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="rounded-lg bg-white/60 px-4 py-3 text-sm font-medium text-gray-800"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            <Link
              to="/contacto"
              className="mt-10 rounded-full bg-cabina-red px-8 py-4 text-center text-sm font-bold uppercase tracking-[0.2em] text-white"
            >
              Contacto
            </Link>
          </div>
        </div>
      )}

      <style>{`
        .nav-link {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 1.2rem;
          font-weight: 700;
          font-style: italic;
          letter-spacing: 0.02em;
          color: #1a1a1a;
          position: relative;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          width: 0;
          height: 1.5px;
          background: #C8102E;
          transform: translateX(-50%);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .nav-link:hover { color: #C8102E; transform: translateY(-2px); }
        .nav-link:hover::after { width: 100%; }
        .nav-link--active { color: #C8102E; }
        .nav-link--active::after { width: 100%; }

        @keyframes fadeIn {
          from { opacity: 0; transform: scale(1.04); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in { animation: fadeIn 0.45s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>
    </header>
  );
};

export default Header;
